import React from 'react';
import Modal from './Modal';

interface Attorney {
  id: string;
  name: string;
  rating: number;
  description: string;
  specialties: string[];
  location: string;
  yearsExperience: number;
  badges: {
    superStar: boolean; // 5-star reviews
    responsive: boolean; // responds within 24 hours
  };
  consultationFee: string;
  image: string;
}

interface AttorneyMarketplaceModalProps {
  open: boolean;
  onClose: () => void;
}

const AttorneyMarketplaceModal: React.FC<AttorneyMarketplaceModalProps> = ({ open, onClose }) => {
  // Mock attorney data
  const attorneys: Attorney[] = [
    {
      id: '1',
      name: 'Sarah Mitchell, Esq.',
      rating: 5,
      description: 'Specializing in comprehensive estate planning and living trusts for high-net-worth individuals. Over 15 years of experience helping families protect their legacy and minimize tax burden.',
      specialties: ['Living Trusts', 'Estate Planning', 'Tax Planning', 'Asset Protection'],
      location: 'San Francisco, CA',
      yearsExperience: 15,
      badges: {
        superStar: true,
        responsive: true
      },
      consultationFee: 'Free initial consultation',
      image: '/api/placeholder/80/80'
    },
    {
      id: '2',
      name: 'Michael Chen, J.D.',
      rating: 5,
      description: 'Expert in revocable living trusts and probate avoidance strategies. Committed to providing personalized legal solutions that protect your family\'s future.',
      specialties: ['Revocable Trusts', 'Probate Law', 'Elder Law', 'Business Succession'],
      location: 'Los Angeles, CA',
      yearsExperience: 12,
      badges: {
        superStar: true,
        responsive: false
      },
      consultationFee: '$200 consultation',
      image: '/api/placeholder/80/80'
    },
    {
      id: '3',
      name: 'Jennifer Rodriguez, Esq.',
      rating: 4,
      description: 'Dedicated to helping families navigate complex estate planning decisions. Specializes in trust administration and multi-generational wealth planning.',
      specialties: ['Trust Administration', 'Wealth Planning', 'Family Law', 'Real Estate Law'],
      location: 'Austin, TX',
      yearsExperience: 8,
      badges: {
        superStar: false,
        responsive: true
      },
      consultationFee: 'Free initial consultation',
      image: '/api/placeholder/80/80'
    },
    {
      id: '4',
      name: 'Robert Thompson, J.D.',
      rating: 5,
      description: 'Former IRS attorney now helping clients with sophisticated estate planning strategies. Expert in tax-efficient trust structures and charitable planning.',
      specialties: ['Tax Law', 'Charitable Trusts', 'IRS Representation', 'Estate Tax Planning'],
      location: 'New York, NY',
      yearsExperience: 20,
      badges: {
        superStar: true,
        responsive: true
      },
      consultationFee: '$300 consultation',
      image: '/api/placeholder/80/80'
    },
    {
      id: '5',
      name: 'Lisa Park, Esq.',
      rating: 4,
      description: 'Compassionate estate planning attorney focused on making the legal process accessible and understandable for all families. Specializes in simple to moderate complexity trusts.',
      specialties: ['Simple Trusts', 'Wills', 'Healthcare Directives', 'Family Planning'],
      location: 'Seattle, WA',
      yearsExperience: 6,
      badges: {
        superStar: false,
        responsive: true
      },
      consultationFee: 'Free initial consultation',
      image: '/api/placeholder/80/80'
    }
  ];

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  const SuperStarIcon = () => (
    <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
      <span className="text-yellow-500">⭐</span>
      <span>5-Star Expert</span>
    </div>
  );

  const ResponsiveIcon = () => (
    <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
      <span className="text-green-500">⚡</span>
      <span>24h Response</span>
    </div>
  );

  const handleContactAttorney = (attorney: Attorney) => {
    // TODO: Implement contact functionality
    console.log('Contacting attorney:', attorney.name);
    alert(`Contacting ${attorney.name}. This would integrate with the attorney's contact system.`);
  };

  return (
    <Modal open={open} onClose={onClose} className="max-w-4xl">
      <div className="p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Trust Attorney Marketplace</h2>
        <p className="text-gray-600 mb-6">
          Connect with experienced estate planning attorneys who specialize in living trusts and asset protection.
        </p>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {attorneys.map((attorney) => (
            <div key={attorney.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* Attorney Image */}
                <img
                  src={attorney.image}
                  alt={attorney.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                />

                {/* Attorney Details */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{attorney.name}</h3>
                      <p className="text-sm text-gray-600">{attorney.location} • {attorney.yearsExperience} years experience</p>
                    </div>
                    <div className="text-right">
                      {renderStars(attorney.rating)}
                      <p className="text-sm text-gray-600 mt-1">{attorney.consultationFee}</p>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex gap-2 mb-3">
                    {attorney.badges.superStar && <SuperStarIcon />}
                    {attorney.badges.responsive && <ResponsiveIcon />}
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 text-sm mb-3 leading-relaxed">{attorney.description}</p>

                  {/* Specialties */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">SPECIALTIES</p>
                    <div className="flex flex-wrap gap-2">
                      {attorney.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="bg-cyan-100 text-cyan-800 px-2 py-1 rounded text-xs font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Contact Button */}
                  <button
                    onClick={() => handleContactAttorney(attorney)}
                    className="bg-cyan-700 hover:bg-cyan-800 text-white px-4 py-2 rounded font-medium text-sm transition-colors"
                  >
                    Contact Attorney
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            End of an Era partners with qualified attorneys to provide you with expert legal guidance. 
            We do not provide legal advice and recommend consulting with a licensed attorney for your specific situation.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default AttorneyMarketplaceModal; 